import { getTodayParisDateString } from "@/shared/utils/date.utils";

/**
 * Fenêtre de dates acceptée par les modèles comptables de yeati-api.
 *
 * Miroir de `SUPPORTED_DATE_SPAN = 10.years` (Core::BankMovement,
 * Core::LedgerAllocation, Core::Settlement) : une date hors de
 * [aujourd'hui - 10 ans, aujourd'hui + 10 ans] est refusée en 422 avec
 * « Date doit être comprise entre <min> et <max> ». Les deux constantes doivent
 * être changées ENSEMBLE.
 *
 * Ces bornes sont une aide à la saisie, pas une validation : le collage, le
 * clavier et l'autofill passent au travers. La gestion du 422 reste donc
 * nécessaire partout où elles sont posées.
 */
export const SUPPORTED_DATE_SPAN_YEARS = 10;

export type AccountingDateBounds = {
  readonly min: string
  readonly max: string
};

/**
 * Décalage d'années sur le calendrier plutôt que sur un timestamp : `setFullYear`
 * fait déborder le 29 février sur le 1er mars, là où ActiveSupport ramène au 28.
 */
const shiftYears = (isoDate: string, years: number): string => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const shiftedYear = year + years;
  const lastDayOfMonth = new Date(Date.UTC(shiftedYear, month, 0)).getUTCDate();
  const clampedDay = Math.min(day, lastDayOfMonth);
  return [
    String(shiftedYear).padStart(4, "0"),
    String(month).padStart(2, "0"),
    String(clampedDay).padStart(2, "0"),
  ].join("-");
};

/**
 * Le jour de référence est celui de Paris, pas celui du navigateur : la fenêtre
 * est calculée côté serveur en `Time.zone`, donc un admin dans un autre fuseau
 * (Antilles, Réunion) ou n'importe qui autour de minuit se verrait refuser un
 * jour légitime, ou proposer un jour que l'API rejette.
 */
export const getAccountingDateBounds = (
  todayIso: string = getTodayParisDateString(),
): AccountingDateBounds => ({
  min: shiftYears(todayIso, -SUPPORTED_DATE_SPAN_YEARS),
  max: shiftYears(todayIso, SUPPORTED_DATE_SPAN_YEARS),
});

/**
 * Calculées une fois à l'import : un onglet laissé ouvert plusieurs jours garde
 * la fenêtre du jour d'ouverture, écart négligeable sur un span de ±10 ans.
 */
export const ACCOUNTING_DATE_BOUNDS = getAccountingDateBounds();
